import { Injectable } from '@angular/core';
import { GIS_USER_TOKEN, GIS_SERVICES_LIST, ARCGIS_USER_ORG_ID } from 'app/main/main-resolver.service';
import { ARCGIS_CONSTANTS, normalizePortalUrl } from '../constants/arcgis-constants';
import { ServiceAuthInfo, ServiceTokenContext } from '../models/arcgis-types';
import { GisServiceList } from '../models/gis-service-list';

@Injectable({
    providedIn: 'root'
})
export class ArcGISTokenManagerService {

    public getTokenForService(serviceUrl: string, context?: ServiceTokenContext, forceSendToken = false): string | null {
        if (forceSendToken) {
            return sessionStorage.getItem(GIS_USER_TOKEN);
        }

        const authInfo = this.detectAuthRequirements(serviceUrl, context);

        if (!authInfo.requiresToken) {
            return null;
        }

        if (authInfo.authType === 'same-portal') {
            return sessionStorage.getItem(GIS_USER_TOKEN);
        }

        return null;
    }

    public detectAuthRequirements(serviceUrl: string, context?: ServiceTokenContext): ServiceAuthInfo {
        if (!serviceUrl) {
            return {
                requiresToken: false,
                authType: 'none',
                reason: 'No service URL provided'
            };
        }

        if (this.isPublicService(context)) {
            return {
                requiresToken: false,
                authType: 'none',
                reason: 'Public service'
            };
        }

        if (context?.origin) {
            return {
                requiresToken: false,
                authType: 'external',
                reason: 'Federated content from external organization'
            };
        }

        const portalHost = this.extractHostname(this.getPortalUrl());
        const serviceHost = this.extractHostname(serviceUrl);
        const hasToken = !!sessionStorage.getItem(GIS_USER_TOKEN);

        if (hasToken && (this.isSameHost(portalHost, serviceHost) || this.isOwnedHostedService(serviceUrl, serviceHost))) {
            return {
                requiresToken: true,
                authType: 'same-portal',
                reason: 'User-owned portal or AGOL-hosted own-org service'
            };
        }

        return {
            requiresToken: false,
            authType: 'external',
            reason: 'External host - token not applicable'
        };
    }

    public buildUrlWithToken(baseUrl: string, context?: ServiceTokenContext, forceSendToken = false): string {
        const token = this.getTokenForService(baseUrl, context, forceSendToken);
        const separator = baseUrl.includes('?') ? '&' : '?';
        const formatParam = ARCGIS_CONSTANTS.QUERY_PARAMS.FORMAT + '=' + ARCGIS_CONSTANTS.FORMAT.JSON;

        if (token) {
            return baseUrl + separator + formatParam + '&' + ARCGIS_CONSTANTS.QUERY_PARAMS.TOKEN + '=' + token;
        }

        return baseUrl + separator + formatParam;
    }

    public appendTokenToParams(params: URLSearchParams, serviceUrl: string, context?: ServiceTokenContext, forceSendToken = false): void {
        const token = this.getTokenForService(serviceUrl, context, forceSendToken);
        if (token) {
            params.append(ARCGIS_CONSTANTS.QUERY_PARAMS.TOKEN, token);
        }
    }

    public async fetchWithAuthRetry(
        buildUrl: (forceSendToken: boolean) => string,
        init?: RequestInit
    ): Promise<Response> {
        const firstUrl = buildUrl(false);
        const firstResponse = await fetch(firstUrl, init);

        const error = await this.peekArcGISError(firstResponse);
        if (error?.code !== ARCGIS_CONSTANTS.ERROR_CODES.TOKEN_REQUIRED) {
            return firstResponse;
        }

        const retryUrl = buildUrl(true);
        if (retryUrl === firstUrl) {
            return firstResponse;
        }

        return fetch(retryUrl, init);
    }

    private async peekArcGISError(response: Response): Promise<{ code: number; message?: string } | null> {
        try {
            const body: unknown = await response.clone().json();
            if (!body || typeof body !== 'object') {
                return null;
            }
            const err = (body as { error?: unknown }).error;
            if (!err || typeof err !== 'object') {
                return null;
            }
            const errObj = err as { code?: unknown; message?: unknown };
            if (typeof errObj.code !== 'number') {
                return null;
            }
            return {
                code: errObj.code,
                message: typeof errObj.message === 'string' ? errObj.message : undefined
            };
        } catch {
            return null;
        }
    }

    private isPublicService(context?: ServiceTokenContext): boolean {
        if (!context) {
            return false;
        }

        if (context.contentStatus === ARCGIS_CONSTANTS.CONTENT_STATUS.PUBLIC_AUTHORITATIVE) {
            return true;
        }

        if (context.access === ARCGIS_CONSTANTS.ACCESS_LEVELS.PUBLIC && !context.origin) {
            return true;
        }

        return false;
    }

    private isSameHost(portalHost: string, serviceHost: string): boolean {
        return portalHost.toLowerCase() === serviceHost.toLowerCase();
    }

    private isOwnedHostedService(serviceUrl: string, serviceHost: string): boolean {
        const lowerHost = serviceHost.toLowerCase();
        const isAGOLHosted = /^services\d*\.arcgis\.com$/.test(lowerHost) || lowerHost === 'tiles.arcgis.com';

        if (!isAGOLHosted) {
            return false;
        }

        const userOrgId = sessionStorage.getItem(ARCGIS_USER_ORG_ID);
        const urlOrgId = this.extractAGOLOrgId(serviceUrl);

        return !!userOrgId && !!urlOrgId && userOrgId.toLowerCase() === urlOrgId.toLowerCase();
    }

    private extractAGOLOrgId(url: string): string | null {
        try {
            const parts = new URL(url).pathname.split('/').filter(segment => segment.length > 0);
            return parts[0] ?? null;
        } catch {
            return null;
        }
    }

    private extractHostname(url: string): string {
        try {
            const urlObj = new URL(url);
            return urlObj.hostname;
        } catch {
            return '';
        }
    }

    private getPortalUrl(): string {
        const servicesListJson = sessionStorage.getItem(GIS_SERVICES_LIST);
        if (servicesListJson) {
            try {
                const servicesList: GisServiceList[] = JSON.parse(servicesListJson);
                const identityService = servicesList.find(
                    (s: GisServiceList) => s.serviceType === ARCGIS_CONSTANTS.SERVICE_TYPES.IDENTITY
                );
                if (identityService?.url) {
                    return normalizePortalUrl(identityService.url);
                }
            } catch {
                return ARCGIS_CONSTANTS.DEFAULT_PORTAL_URL;
            }
        }
        return ARCGIS_CONSTANTS.DEFAULT_PORTAL_URL;
    }
}
